<template>
  <div
    v-if="products.length"
    class="product-siblings"
    :class="{
      'product-siblings--grid': localSiblings.grid,
    }"
  >
    <div
      class="product-siblings__label-group text-body-s-regular mb-m"
      role="text"
    >
      <span
        v-if="siblingDescription"
        class="visually-hidden"
        v-text="`View ${formatProductTitle(product.title)} in different variations. `"
      />
      <span
        v-if="localSiblings.label"
        class="product-siblings__label"
        v-text="localSiblings.label"
      />
      <span
        v-if="siblingDescription && !braceletFinder"
        v-text="`: ${siblingDescription}`"
      />
      <span
        v-if="siblingDescription && braceletFinder"
        v-text="`${siblingDescription}`"
      />
    </div>

    <splide
      v-if="!braceletFinder"
      :key="splideConfig.destroy"
      class="product-siblings__splide"
      :options="splideConfig"
      @splide:drag="setDragging"
      @splide:dragged="unsetDragging"
    >
      <splide-slide
        v-for="(sibling) of products"
        :key="sibling.id"
        class="product-siblings__sibling"
        :class="{
          'product-siblings__sibling--active': sibling.id ===
            activeProduct.id || sibling.id === product.id,
          [$class('disabled')]: !sibling.available
        }"
      >
        <a
          :href="sibling.url"
          :aria-label="`View product in ${getSiblingDescription(sibling)}`"
          @click="refreshPage ? null : updateProduct($event, sibling)"
          @mouseenter="isIOS ? null : updateActiveProduct(sibling)"
          @mouseleave="isIOS ? null : updateActiveProduct(product)"
          @focusin="updateActiveProduct(sibling)"
          @focusout="updateActiveProduct(product)"
        >
          <responsive-image
            class="product-siblings__image"
            :image="getSiblingImage(sibling)"
            min-max="98-245"
            ratio="1:1"
            aria-hidden="true"
          />
        </a>
      </splide-slide>
    </splide>

    <div
      v-for="(sibling) of products"
      v-else
      :key="sibling.id"
      class="product-siblings__sibling"
      :class="{
        'product-siblings__sibling--active': sibling.id ===
          activeProduct.id || sibling.id === product.id,
        [$class('disabled')]: !sibling.available
      }"
    >
      <a
        :href="sibling.url"
        @click="refreshPage ? null : updateProduct($event, sibling)"
        @mouseenter="isIOS ? null : updateActiveProduct(sibling)"
        @mouseleave="isIOS ? null : updateActiveProduct(product)"
      >
        <responsive-image
          class="product-siblings__image"
          :image="getSiblingImage(sibling)"
          min-max="98-245"
          ratio="1:1"
        />
      </a>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product siblings (product-siblings)
 * -----------------------------------------------------------------------------
 * Display products that belong to the same family.
 *
 * @param {Object} product - Shopify product
 * @param {Boolean} [refreshPage] - Direct link to product sibling, causing a
 * full page refresh
 * @param {Object} siblings - Product family metaobject data
 * @param {String} siblings.label - Name of the option label on PDP form
 * @param {Array} siblings.products - Array of product objects
 * @param {Boolean} siblings.grid - Display siblings in grid layout
 *
 * @emits update-product
 */
import { Splide, SplideSlide } from '@splidejs/vue-splide'

import ResponsiveImage from '~global/images/responsive-image'

import metaobjectQuery from '~graphql/metaobject.gql'

import { getMetafield } from '~/helpers/general'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { formatProduct, formatProductTitle } from '~/helpers/format'
import { convertToHandle } from '~/helpers/convert'
import { mapState } from 'vuex'

export default {
  name: 'ProductSiblings',

  components: {
    ResponsiveImage,
    Splide,
    SplideSlide,
  },

  inject: {
    context: {
      default: '',
    },
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },
    refreshPage: {
      type: Boolean,
      default: false,
    },
    siblings: {
      type: Object,
      default: null,
    },
    braceletFinder: {
      type: Boolean,
      default: false,
    },
    stoneImages: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'update-active-product': (product) => {
      if (product) {
        return true
      }
      // eslint-disable-next-line no-console
      console.warn('Invalid sibling update payload. Product object is required.')
      return false
    },
    'update-product': (product) => {
      if (product) {
        return true
      }
      // eslint-disable-next-line no-console
      console.warn('Invalid sibling update payload. Product object is required.')
      return false
    },
  },

  data() {
    return {
      activeProduct: this.product,
      isDragging: false,
      debounce: false,
      products: this.siblings?.products || [],
      queriedSiblings: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if device is an iPhone or iPad
     * @returns {boolean}
     */
    isIOS() {
      return this.isMobile ? (/iPad|iPhone|iPod/i).test(navigator.userAgent) : false
    },

    /**
     * Compute siblings object
     * @returns {Object|boolean}
     */
    localSiblings() {
      const object = {
        ...this.siblings || this.queriedSiblings || {},
      }
      if (this.context !== 'PDP') {
        object.grid = false
      }
      return object
    },

    /**
     * Compute the description that sits next to the label.
     * @returns {*}
     */
    siblingDescription() {
      if (!this.activeProduct) {
        return ''
      }

      if (this.braceletFinder) {
        const siblingOptions = this.activeProduct.options
        const stoneOption = siblingOptions.find((option) => option.name === 'Stone')?.values[0]
        const metal = siblingOptions.find((option) => option.name === 'Metal')?.values[0]
        const stoneOrMetal = stoneOption ? stoneOption : metal
        const stoneDescription = this.stoneImages.find((image) => image.stone_name === stoneOrMetal)

        if (stoneDescription) {
          return stoneDescription.stone_name
        }
      }

      const meta = this.getSiblingDescription(this.activeProduct)
      return this.isDragging ? '' : meta || ''
    },

    /**
     * Compute splide config. The config is dynamic based off the local
     * siblings computed value
     * @returns {Object}
     */
    splideConfig() {
      return {
        arrows: false,
        drag: true,
        flickPower: 200,
        keyboard: 'focused',
        autoWidth: true,
        gap: 'var(--spacing-xs)',
        label: this.localSiblings?.label || '',
        padding: { right: 'var(--layout-margin)' },
        pagination: false,
        snap: false,
        type: 'slide',
        wheel: 'true',
        wheelMinThreshold: 20,
        wheelSleep: 100,
        destroy: this.localSiblings.grid,
      }
    },
  },

  watch: {

    /**
     * Update the active product if the product prop changes.
     */
    product(product, oldProduct) {
      if (this.activeProduct.id !== product.id) {
        this.activeProduct = product
      }

      if (
        oldProduct.id !== product.id &&
        !this.products.some((sibling) => sibling.id === product.id)
      ) {
        this.getSiblings()
      }
    },
  },

  created() {
    if (this.braceletFinder) {
      this.getFinderSiblings()
    } else if (this.siblings) {
      this.sortProducts()
    } else {
      this.getSiblings()
    }
  },

  methods: {
    formatProductTitle,

    /**
     * Get queried siblings if siblings prop was not provided.
     */
    async getSiblings() {
      if (this.siblings || !this.product) {
        return
      }

      const familyTag = this.product.tags.find((tag) => tag.includes('product-family:'))

      if (!familyTag) {
        this.products = []
        return
      }

      const familyHandle = familyTag.replace('product-family:', '')
      let response
      try {
        response = await cnvs.Query({
          query: metaobjectQuery,
          variables: getVariables({
            handle: {
              type: 'product_family',
              handle: familyHandle,
            },
            metafieldIds: [
              ...getMetafieldIDs('product'),
              ...getMetafieldIDs('variant'),
            ],
          }),
        })

        if (!response?.metaobject) {
          this.products = []
          return
        }

        const queriedSiblings = {
          label: '',
          products: [],
          grid: false,
        }
        response.metaobject.fields.forEach((field) => {
          let value = field.value
          if (field.key === 'products') {
            value = field.references
            value = value.nodes.map(
              (product) => formatProduct({ product }),
            )
          }
          queriedSiblings[field.key] = value
        })

        this.queriedSiblings = queriedSiblings

        this.sortProducts()

        if (this.context === 'drawer') {
          cnvs.EventBus.emit('overlay:update', 'quickAddDrawer')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error: ', error, 'Response: ', response, 'Product: ', this.product)
        cnvs.ReportError('[Product Siblings]', error)
      }
    },

    /**
     * Get the sibling description from active product's metafields
     * @param {object} product - Active product
     */
    getSiblingDescription({ colorOptions, metafields }) {
      let meta = getMetafield({
        metafields: metafields,
        namespace: 'family',
        key: 'description',
      })

      if (!meta && colorOptions && colorOptions[0]) {
        meta = colorOptions[0].values[0]
      }

      return meta || ''
    },

    /**
     * Get bracelet finder siblings.
     */
    async getFinderSiblings() {
      if (!this.product) {
        return
      }

      const urlParams = new URLSearchParams(location.search)

      const selectedMetal = urlParams.get('metal').replace('+', ' ')
      const selectedWidth = urlParams.get('width')
      let response
      try {
        response = await cnvs.Query({
          query: metaobjectQuery,
          variables: getVariables({
            handle: {
              type: 'bracelet_finder',
              handle: convertToHandle(selectedMetal),
            },
            metafieldIds: [
              ...getMetafieldIDs('product'),
              ...getMetafieldIDs('variant'),
            ],
          }),
        })

        if (!response?.metaobject) {
          this.products = []
          return
        }

        const productArray = []
        response.metaobject.fields.forEach((field) => {
          let value = field.value

          if (field.key === selectedWidth) {
            value = field.references
            value = value.nodes.map(
              (product) => formatProduct({ product }),
            )
            productArray.push(value)
          }
        })
        this.queriedSiblings = {
          label: '',
          products: productArray[0],
          grid: true,
        }
        this.sortProducts()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error: ', error, 'Response: ', response, 'Product: ', this.product)
        cnvs.ReportError('[Product Siblings]', error)
      }
    },

    /**
     * Get a product sibling's thumbnail image
     * @param {Object} sibling - A product sibling
     */
    getSiblingImage(sibling) {
      if (this.braceletFinder) {
        const siblingOptions = sibling.options
        const stoneOption = siblingOptions.find((option) => option.name === 'Stone')?.values[0]
        const metal = siblingOptions.find((option) => option.name === 'Metal')?.values[0]
        const stoneOrMetal = stoneOption ? stoneOption : metal
        const siblingImg = this.stoneImages.find((image) => image.stone_name === stoneOrMetal)

        if (siblingImg) {
          return siblingImg.stone_image
        }
      }
      const thumbnail = getMetafield({
        metafields: sibling.metafields,
        namespace: 'family',
        key: 'thumbnail',
      })

      if (thumbnail?.src || thumbnail?.url) {
        return thumbnail
      }

      return thumbnail?.reference?.image || sibling.featuredImage
    },

    /**
     * Set dragging
     */
    setDragging() {
      this.isDragging = true
    },

    /**
     * Remove drag
     */
    unsetDragging() {
      this.isDragging = false
    },

    /**
     * Update the active product. The active product is used to display the
     * hovered label.
     * @param {Object} product - Formatted product
     */
    updateActiveProduct(product) {
      this.activeProduct = product

      if (this.debounce) {
        clearTimeout(this.debounce)
      }

      this.debounce = setTimeout(() => {
        this.$emit('update-active-product', product)
        this.debounce = false
      }, this.$timing('quick'))
    },

    /**
     * Emit product update event
     * @param {MouseEvent} event - Click event
     * @param {Object} sibling - The product to emit.
     */
    updateProduct(event, sibling) {
      if (this.refreshPage) {
        return
      }
      event.preventDefault()
      this.$emit('update-product', sibling)
    },

    /**
     * Place main product at the front of the siblings array on mount
     */
    sortProducts() {
      if (!this.localSiblings) {
        return
      }

      const id = this.product.id
      const products = this.localSiblings.products.slice()
      const index = products.findIndex(
        (product) => product.id === id,
      )

      // Product doesn't exist or is already at the front
      if (index <= 0) {
        this.products = products
        return
      }

      products.splice(index, 1)
      products.unshift({ ...this.product })

      this.products = products
    },
  },
}
</script>

<style lang="scss">
@import './product-siblings';
</style>
